import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-attempt";

/**
 * React.lazy wrapper that survives stale/failed chunk downloads.
 * A failed dynamic import (after a new deploy, or a flaky network) normally
 * blanks the screen with "Importing a module script failed."
 * We retry once, then force a single hard reload to fetch the fresh manifest.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (error) {
      // second chance: transient network failure
      try {
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch (retryError) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          // never resolves; the page is reloading
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
